import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { styled } from '../../../../../shared/Theme/theme';
import { DropDown } from '../../../../../shared/inputs/base';
import Button from '../../../../../shared/SmallComponents/Button';
import Loader from '../../../../../shared/SmallComponents/Loader';
import { businessPartner } from '../../../../../../models/entities/businessPartner/businessPartner';
import { bpAccountingResponse, bpAccountingRow } from '../../../../../../models/entities/businessPartner/accounting';
import { settings as shipmentSettings } from '../../../../../../models/entities/shipment/shipment';
import { localSDK as client } from '../../../../../../sdk';
import {
    BpInvoiceIssuer,
    paymentTermsDays,
    paymentTermsLabelFromCode
} from './invoicePdfTypes';
import { getBpInvoicePdfFile } from './invoicePdfUtils';
import {
    bpInvoiceSummary,
    fetchShipmentInvoiceContext,
    resolveBillingLinesForInvoice
} from '../../../../commonSubEntities/accounting/invoiceLineTracking';

export type DownloadInvoiceModalProps = {
    businessPartner: businessPartner;
    row: bpAccountingRow;
    accountingData: bpAccountingResponse;
    issuer: BpInvoiceIssuer;
    shipmentSettings?: shipmentSettings;
    onClose: () => void;
};

const NEW_INVOICE_VALUE = '__new__';

const DownloadInvoiceModal = ({
    businessPartner,
    row,
    accountingData,
    issuer,
    shipmentSettings,
    onClose
}: DownloadInvoiceModalProps) => {
    const [loading, setLoading] = useState(false);
    const [loadingInvoices, setLoadingInvoices] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [invoices, setInvoices] = useState<bpInvoiceSummary[]>([]);
    const [uninvoicedCount, setUninvoicedCount] = useState(0);
    const [selectedInvoiceKey, setSelectedInvoiceKey] = useState<string>(NEW_INVOICE_VALUE);

    useEffect(() => {
        setLoadingInvoices(true);
        void fetchShipmentInvoiceContext(row.shipmentId, businessPartner.id)
            .then((ctx) => {
                setInvoices(ctx.invoices);
                setUninvoicedCount(ctx.uninvoicedCount);
                if (ctx.uninvoicedCount > 0) {
                    setSelectedInvoiceKey(NEW_INVOICE_VALUE);
                } else if (ctx.invoices[0]) {
                    setSelectedInvoiceKey(ctx.invoices[0].id);
                }
            })
            .finally(() => setLoadingInvoices(false));
    }, [row.shipmentId, businessPartner.id]);

    const invoiceOptions = useMemo(() => {
        const opts: Array<{ value: string; text: string }> = [];
        if (uninvoicedCount > 0) {
            opts.push({
                value: NEW_INVOICE_VALUE,
                text: `New invoice (${uninvoicedCount} uninvoiced line${uninvoicedCount === 1 ? '' : 's'})`
            });
        }
        invoices.forEach((inv) => {
            const sent = inv.sentAt ? ` · sent ${moment(inv.sentAt).format('DD/MM/YY')}` : '';
            opts.push({
                value: inv.id,
                text: `${inv.invoiceNumber}${sent} (${inv.billingLineIds.length} lines)`
            });
        });
        return opts;
    }, [invoices, uninvoicedCount]);

    const serviceTypeLabel = (code: string) =>
        shipmentSettings?.billingLineServiceType?.find((s) => s.code === code)?.name || code;

    const handleDownload = async () => {
        if (!invoiceOptions.length) {
            setError('No invoice available to download.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const invoiceId = selectedInvoiceKey === NEW_INVOICE_VALUE ? null : selectedInvoiceKey;
            const resolved = await resolveBillingLinesForInvoice({
                shipmentId: row.shipmentId,
                businessPartnerId: businessPartner.id,
                invoiceId
            });
            const shipment = await client.shipments().fetchById(row.shipmentId);
            const invoiceDate = row.shipmentDate ? new Date(row.shipmentDate) : new Date();
            const termsCode = businessPartner.paymentTerms;
            const days = paymentTermsDays(termsCode);
            const dueDate =
                days != null && row.shipmentDate
                    ? new Date(new Date(row.shipmentDate).getTime() + days * 86400000)
                    : row.dueDate
                      ? new Date(row.dueDate)
                      : null;

            const file = await getBpInvoicePdfFile({
                businessPartner,
                shipment,
                billingLines: resolved.billingLines,
                issuer,
                invoiceNumber: resolved.invoiceNumber,
                invoiceDate,
                dueDate,
                paymentTermsLabel: paymentTermsLabelFromCode(termsCode) || accountingData.kpis.paymentTermsLabel,
                currency: row.currency || accountingData.kpis.currency,
                serviceTypeLabel
            });

            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            onClose();
        } catch (e) {
            setError((e as Error)?.message || 'Failed to download invoice.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Body>
            <Hint>
                New invoices include only billing lines not yet invoiced. Choose a previous invoice to download the same
                snapshot again.
            </Hint>

            <FieldLabel>Invoice to download</FieldLabel>
            {loadingInvoices ? (
                <Loader width="20px" wrapperWidth="auto" marginTop="0" showText={false} inline />
            ) : (
                <DropDown
                    options={invoiceOptions}
                    value={selectedInvoiceKey}
                    onChange={(v: string) => setSelectedInvoiceKey(v)}
                    width="100%"
                />
            )}

            {error && <ErrorText>{error}</ErrorText>}
            <Footer>
                <Button buttonType="hollow" height="34px" minWidth="72px" onClick={onClose} disabled={loading}>
                    <span>Cancel</span>
                </Button>
                <Button
                    buttonType="filled"
                    height="34px"
                    minWidth="120px"
                    onClick={() => void handleDownload()}
                    disabled={loading || loadingInvoices || !invoiceOptions.length}
                >
                    {loading ? (
                        <Loader width="20px" wrapperWidth="auto" marginTop="0" showText={false} inline />
                    ) : (
                        <span>Download PDF</span>
                    )}
                </Button>
            </Footer>
        </Body>
    );
};

const Body = styled.div`
    padding: 8px 4px 0;
`;

const Hint = styled.p`
    font-size: 12px;
    color: #6b7280;
    margin: 0 0 12px;
    line-height: 1.45;
`;

const FieldLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
    color: #374151;
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-top: 20px;
    flex-shrink: 0;
`;

const ErrorText = styled.div`
    color: #b91c1c;
    font-size: 12px;
    margin-top: 10px;
`;

export default DownloadInvoiceModal;
